import * as t from 'io-ts';

import {makeEnumRuntimeType} from '../../../../../libs/shared/helpers/src/typescript/enumHelpers';
import {AppRoutesPrefixesEnum} from '../../../../components/appRoutesPrefixes';
import {standaloneComposerPath} from '../../../../helpers/composerHelper';
import {RVisualRecipient} from '../../../../models/api/visualRecipientModel';
import {RAnalyticsParameters} from '../../analytics/parameters/analyticsParameters';
import {RCalendarsParameters} from '../../calendars/calendarsParameters';
import {RContactsManagerParameters} from '../../contacts/contactsManager/parameters/contactsManagerParameters';
import {RInboxesParameters} from '../../inboxes/parameters/inboxesParameters';

/*
 * Runtime types.
 */

export enum AppContentTypesEnum {
  INBOXES = 'INBOXES',
  CALENDARS = 'CALENDARS',
  CONTACTS = 'CONTACTS',
  ANALYTICS = 'ANALYTICS',
  SEQUENCES = 'SEQUENCES',
  AUDIT_TRAIL = 'AUDIT_TRAIL',
  SETTINGS = 'SETTINGS',
  VISITOR_VIEWER = 'VISITOR_VIEWER',
  STANDALONE_COMPOSER = 'STANDALONE_COMPOSER',
  VISITOR_EXPERIENCE = 'VISITOR_EXPERIENCE',
  VISITOR_EXPERIENCE_V3 = 'VISITOR_EXPERIENCE_V3',
}

export const RAppContentType = makeEnumRuntimeType(AppContentTypesEnum);

const RAppParametersBase = t.type({
  contentType: RAppContentType,
});

function makeAppLegacyParametersType<T extends AppContentTypesEnum>(contentType: T) {
  return t.intersection([
    RAppParametersBase,
    t.type({
      contentType: t.literal(contentType),
    }),
    t.partial({
      path: t.string,
    }),
  ]);
}

const RAppInboxesParameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.INBOXES),
  }),
  t.partial({
    parameters: RInboxesParameters,
  }),
]);

const RAppCalendarsParameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.CALENDARS),
  }),
  t.partial({
    parameters: RCalendarsParameters,
  }),
]);

const RAppContactsParameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.CONTACTS),
  }),
  t.partial({
    parameters: RContactsManagerParameters,
  }),
]);

const RAppAnalyticsParameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.ANALYTICS),
  }),
  t.partial({
    parameters: RAnalyticsParameters,
  }),
]);

const RAppSequencesParameters = makeAppLegacyParametersType(AppContentTypesEnum.SEQUENCES);
const RAppAuditTrailParameters = makeAppLegacyParametersType(AppContentTypesEnum.AUDIT_TRAIL);
const RAppSettingsParameters = makeAppLegacyParametersType(AppContentTypesEnum.SETTINGS);

const RAppVisitorViewerParameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.VISITOR_VIEWER),
  }),
]);

const RAppVisitorExperienceParameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.VISITOR_EXPERIENCE),
  }),
]);

const RAppVisitorExperienceV3Parameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.VISITOR_EXPERIENCE_V3),
  }),
]);

const RAppStandaloneComposerParameters = t.intersection([
  RAppParametersBase,
  t.type({
    contentType: t.literal(AppContentTypesEnum.STANDALONE_COMPOSER),
  }),
  t.partial({
    mailtoOptions: t.type({
      from: t.string,
      recipients: t.readonlyArray(RVisualRecipient),
      subject: t.string,
      trustedHtml: t.string,
      text: t.string,
    }),
  }),
]);

const RAppParametersDirty = t.taggedUnion('contentType', [
  RAppInboxesParameters,
  RAppCalendarsParameters,
  RAppContactsParameters,
  RAppAnalyticsParameters,
  RAppSequencesParameters,
  RAppAuditTrailParameters,
  RAppSettingsParameters,
  RAppVisitorViewerParameters,
  RAppVisitorExperienceParameters,
  RAppVisitorExperienceV3Parameters,
  RAppStandaloneComposerParameters,
]);

/*
 * Types.
 */

interface AppInboxesParameters extends Readonly<t.TypeOf<typeof RAppInboxesParameters>> {}
interface AppCalendarsParameters extends Readonly<t.TypeOf<typeof RAppCalendarsParameters>> {}
interface AppContactsParameters extends Readonly<t.TypeOf<typeof RAppContactsParameters>> {}
interface AppAnalyticsParameters extends Readonly<t.TypeOf<typeof RAppAnalyticsParameters>> {}
interface AppSequencesParameters extends Readonly<t.TypeOf<typeof RAppSequencesParameters>> {}
interface AppAuditTrailParameters extends Readonly<t.TypeOf<typeof RAppAuditTrailParameters>> {}
interface AppSettingsParameters extends Readonly<t.TypeOf<typeof RAppSettingsParameters>> {}
interface AppVisitorViewerParameters extends Readonly<t.TypeOf<typeof RAppVisitorViewerParameters>> {}
interface AppVisitorExperienceParameters extends Readonly<t.TypeOf<typeof RAppVisitorExperienceParameters>> {}
interface AppVisitorExperienceV3Parameters
  extends Readonly<t.TypeOf<typeof RAppVisitorExperienceV3Parameters>> {}
interface AppStandaloneComposerParameters
  extends Readonly<t.TypeOf<typeof RAppStandaloneComposerParameters>> {}

export type AppParameters =
  | AppInboxesParameters
  | AppCalendarsParameters
  | AppContactsParameters
  | AppAnalyticsParameters
  | AppSequencesParameters
  | AppAuditTrailParameters
  | AppSettingsParameters
  | AppVisitorViewerParameters
  | AppVisitorExperienceParameters
  | AppVisitorExperienceV3Parameters
  | AppStandaloneComposerParameters;

export const RAppParameters = t.clean<AppParameters>(RAppParametersDirty as any);

/*
 * Constants.
 */

export const defaultPathsByContentType: {[contentType in AppContentTypesEnum]: string} = {
  [AppContentTypesEnum.INBOXES]: `/${AppRoutesPrefixesEnum.INBOXES}`,
  [AppContentTypesEnum.CALENDARS]: `/${AppRoutesPrefixesEnum.CALENDAR}`,
  [AppContentTypesEnum.CONTACTS]: `/${AppRoutesPrefixesEnum.CONTACTS_MANAGER}`,
  [AppContentTypesEnum.ANALYTICS]: `/${AppRoutesPrefixesEnum.ANALYTICS}`,
  [AppContentTypesEnum.SEQUENCES]: `/${AppRoutesPrefixesEnum.SEQUENCES}`,
  [AppContentTypesEnum.AUDIT_TRAIL]: `/${AppRoutesPrefixesEnum.AUDIT_TRAIL}`,
  [AppContentTypesEnum.SETTINGS]: `/${AppRoutesPrefixesEnum.SETTINGS}`,
  [AppContentTypesEnum.VISITOR_VIEWER]: `/${AppRoutesPrefixesEnum.VISITORS}`,
  [AppContentTypesEnum.VISITOR_EXPERIENCE]: `/${AppRoutesPrefixesEnum.VISITORS}`,
  [AppContentTypesEnum.VISITOR_EXPERIENCE_V3]: `/${AppRoutesPrefixesEnum.VISITORS}`,
  [AppContentTypesEnum.STANDALONE_COMPOSER]: standaloneComposerPath,
};
